/**
 * HelpDrawer Component — Centered modal with contextual help.
 * Supports both simple (terms/statuses) and rich (sections/statusFlow) content.
 */

import { useEffect, useCallback } from 'react';
import { X, HelpCircle, CheckCircle, XCircle, AlertTriangle, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { HELP_CONTENT } from '../../data/help-content';
import type { HelpSection, HelpSectionItem, HelpStatusFlow } from '../../types';

// ─── Text colorization ──────────────────────────────────────────────────────

const HIGHLIGHTS: Record<string, string> = {
  'Platinum': 'text-blue-400 font-semibold',
  'PLATINUM': 'text-blue-400 font-semibold',
  'Gold': 'text-[#DAA520] font-semibold',
  'GOLD': 'text-[#DAA520] font-semibold',
  'Silver': 'text-[#94A3B8] font-semibold',
  'SILVER': 'text-[#94A3B8] font-semibold',
  'Bronze': 'text-[#CD7F32] font-semibold',
  'BRONZE': 'text-[#CD7F32] font-semibold',
};

function colorizeText(text: string): (string | JSX.Element)[] {
  const pattern = new RegExp(`(${Object.keys(HIGHLIGHTS).join('|')})`, 'g');
  const parts = text.split(pattern);
  return parts.map((part, i) => {
    const cls = HIGHLIGHTS[part];
    if (cls) return <span key={i} className={cls}>{part}</span>;
    return part;
  });
}

// ─── Color maps for badges and sections ──────────────────────────────────────

const BADGE_COLORS: Record<string, string> = {
  blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  green: 'bg-green-500/20 text-green-400 border-green-500/30',
  purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  red: 'bg-red-500/20 text-red-400 border-red-500/30',
  amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
};

type FlowColorSet = { bg: string; text: string; border: string };

const FLOW_STEP_FALLBACK: FlowColorSet = {
  bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/40',
};

const FLOW_STEP_COLORS: Record<string, FlowColorSet> = {
  amber: { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/40' },
  purple: { bg: 'bg-purple-500/15', text: 'text-purple-400', border: 'border-purple-500/40' },
  blue: { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/40' },
  green: { bg: 'bg-green-500/15', text: 'text-green-400', border: 'border-green-500/40' },
  red: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/40' },
  cyan: { bg: 'bg-cyan-500/15', text: 'text-cyan-400', border: 'border-cyan-500/40' },
  orange: { bg: 'bg-orange-500/15', text: 'text-orange-400', border: 'border-orange-500/40' },
};

const ITEM_ICONS = {
  check: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
} as const;

// ─── Sub-components for rich content ─────────────────────────────────────────

function StatusFlowBlock({ flow }: { flow: HelpStatusFlow }) {
  return (
    <section className="rounded-lg border border-border bg-muted/30 p-4">
      {flow.label && (
        <h3 className="mb-3 text-sm font-semibold text-foreground">{flow.label}</h3>
      )}
      {/* Visual flow: badges with arrows */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {flow.steps.map((step, i) => {
          const colors = FLOW_STEP_COLORS[step.color] ?? FLOW_STEP_FALLBACK;
          return (
            <span key={step.value} className="flex items-center gap-2">
              <span className={`rounded-md border px-2.5 py-1 text-xs font-semibold ${colors.bg} ${colors.text} ${colors.border}`}>
                {step.value}
              </span>
              {i < flow.steps.length - 1 && (
                <ArrowRight size={14} className="text-muted-foreground/50" />
              )}
            </span>
          );
        })}
      </div>
      {/* Descriptions */}
      <div className="space-y-1">
        {flow.steps.filter((s) => s.description).map((step) => {
          const colors = FLOW_STEP_COLORS[step.color] ?? FLOW_STEP_FALLBACK;
          return (
            <p key={step.value} className="text-sm">
              <span className={`font-semibold ${colors.text}`}>{step.value}:</span>{' '}
              <span className="text-muted-foreground">{step.description}</span>
            </p>
          );
        })}
      </div>
    </section>
  );
}

function SectionItemBlock({ item }: { item: HelpSectionItem }) {
  const IconComp = item.icon ? ITEM_ICONS[item.icon] : null;
  const iconColor = item.icon === 'check' ? 'text-green-400' : item.icon === 'error' ? 'text-red-400' : 'text-amber-400';

  return (
    <div className="mt-2">
      <p className="text-sm break-words">
        {IconComp && <IconComp size={14} className={`mr-1 inline-block ${iconColor}`} />}
        <span className={`font-semibold ${iconColor}`}>{item.label}:</span>{' '}
        <span className="text-muted-foreground">{item.description}</span>
      </p>
      {item.conditions?.map((cond) => (
        <div key={cond.label} className="ml-4 mt-1">
          <p className={`text-xs font-semibold ${cond.severity === 'ok' ? 'text-green-400' : 'text-red-400'}`}>
            {cond.severity === 'ok' ? '✓ ' : '🚨 '}{cond.label}
          </p>
          <ul className="ml-3 list-disc text-xs text-muted-foreground">
            {cond.items.map((ci) => (
              <li key={ci} className={cond.severity === 'error' ? 'text-red-400/80' : ''}>
                {ci}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function SectionBlock({ section }: { section: HelpSection }) {
  const badgeClasses = section.badgeColor ? BADGE_COLORS[section.badgeColor] : BADGE_COLORS.blue;

  return (
    <div className="mt-4 first:mt-0">
      {/* Section header */}
      <div className="mb-2 flex items-center gap-2">
        {section.badge && (
          <span className={`rounded border px-2 py-0.5 text-[10px] font-bold ${badgeClasses}`}>
            {section.badge}
          </span>
        )}
        <h4 className="text-sm font-bold text-foreground">{section.title}</h4>
      </div>

      {/* Section body */}
      <div className="rounded-lg border border-border bg-muted/20 p-3">
        {section.description && (
          <p className="text-sm text-muted-foreground break-words">
            {section.description.split(/(\b(?:DEBE|NO|ERROR|SIEMPRE)\b)/g).map((part, i) => {
              if (['DEBE', 'NO', 'ERROR', 'SIEMPRE'].includes(part)) {
                return <span key={i} className="font-bold text-red-400">{part}</span>;
              }
              return part;
            })}
          </p>
        )}
        {section.items.map((item) => (
          <SectionItemBlock key={item.label} item={item} />
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

interface HelpDrawerProps {
  pageKey: string;
  isOpen: boolean;
  onClose: () => void;
}

export function HelpDrawer({ pageKey, isOpen, onClose }: HelpDrawerProps) {
  const content = HELP_CONTENT[pageKey];

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !content) return null;

  const hasSections = content.sections && content.sections.length > 0;
  const hasStatusFlow = content.statusFlow != null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="flex flex-col whitespace-normal rounded-xl border border-border bg-card shadow-2xl overflow-hidden"
        style={{ width: 'min(640px, 100%)', maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-border px-6 py-4">
          <div className="flex min-w-0 flex-1 items-start gap-2">
            <HelpCircle size={20} className="mt-0.5 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-foreground">{content.title}</h2>
              {content.description && (
                <p className="text-sm text-muted-foreground break-words">{colorizeText(content.description)}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-5 space-y-5">

          {/* Status Flow (visual arrow badges) */}
          {hasStatusFlow && <StatusFlowBlock flow={content.statusFlow!} />}

          {/* Que puedes hacer aqui */}
          {content.capabilities && content.capabilities.length > 0 && (
            <section className="rounded-lg border border-border bg-muted/30 p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">
                🎯 Que puedes hacer aqui
              </h3>
              <ul className="space-y-2">
                {content.capabilities.map((cap) => (
                  <li key={cap} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-500" />
                    {colorizeText(cap)}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Rich Sections (tab-grouped content) */}
          {hasSections && (
            <section className="space-y-1">
              {content.sections!.map((sec) => (
                <SectionBlock key={sec.title} section={sec} />
              ))}
            </section>
          )}

          {/* Conceptos Clave (flat terms — legacy, still supported) */}
          {content.terms && content.terms.length > 0 && (
            <section className="rounded-lg border border-border bg-muted/30 p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">
                💡 Conceptos Clave
              </h3>
              <dl className="space-y-2">
                {content.terms.map((item) => (
                  <div key={item.term}>
                    <dt className="text-sm font-semibold text-foreground">{item.term}</dt>
                    <dd className="text-sm text-muted-foreground">{colorizeText(item.definition)}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {/* Diccionario de Estados (flat badges — legacy, still supported) */}
          {content.statuses && content.statuses.length > 0 && (
            <section className="rounded-lg border border-border bg-muted/30 p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">
                🏷️ Estados
              </h3>
              <div className="space-y-2.5">
                {content.statuses.map((status) => (
                  <div key={status.value} className="flex items-start gap-3">
                    <Badge variant={status.variant} className="shrink-0 mt-0.5">{status.label}</Badge>
                    <p className="text-sm text-muted-foreground">{colorizeText(status.description)}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* FAQ */}
          {content.faq && content.faq.length > 0 && (
            <section className="rounded-lg border border-border bg-muted/30 p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">
                ❓ Preguntas Frecuentes
              </h3>
              <div className="space-y-3">
                {content.faq.map((item) => (
                  <div key={item.question}>
                    <p className="text-sm font-semibold text-foreground">{item.question}</p>
                    <p className="text-sm text-muted-foreground">{colorizeText(item.answer)}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border px-6 py-3 flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>
            Entendido
          </Button>
        </div>
      </div>
    </div>
  );
}
